import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Button} from 'primeng/button';
import {switchMap, take} from 'rxjs/operators';
import {finalize} from 'rxjs';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {AuditId, ProjectId, SpecId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {Finding, FindingCreationData, FindingType, ObservationTarget} from '@webmate-web-core/shared/types/testmgmt';

/**
 * Reusable button that creates a new (empty) finding for an entity and gives the user feedback
 * while the request is in flight (spinner + disabled). The variable creation parameters are supplied
 * by the caller so the same button works in the workbench and in the console (which has no workbench
 * context).
 *
 * The created finding is fetched before the spinner stops, so the spinner reflects the whole
 * operation (create + load) rather than only the create request. The fully loaded finding is then
 * emitted via {@link findingCreated} so the parent can insert it into its list immediately instead
 * of waiting for the (slower) backend update event. On failure it shows an error toast.
 */
@Component({
    selector: 'wm-add-finding-button',
    standalone: true,
    imports: [Button],
    templateUrl: './add-finding-button.component.html'
})
export class AddFindingButtonComponent {
    @Input({required: true}) projectId!: ProjectId;
    @Input({required: true}) findingType!: FindingType;
    @Input() testRun?: TestRunId;
    @Input() audit?: AuditId;
    @Input() affectedSpecs?: SpecId[];
    @Input() observationTargets?: ObservationTarget[];
    /** Used to build the auto-numbered title "Finding #N" (N = count + 1). */
    @Input() existingFindingsCount = 0;
    @Input() label = 'Add New Finding';

    @Output() findingCreated = new EventEmitter<Finding>();

    creating = false;

    constructor(private readonly testService: TestService,
                private readonly notificationService: WmNotificationService) {}

    create(): void {
        if (this.creating) {
            return;
        }
        this.creating = true;

        const creationData: FindingCreationData = {
            findingType: this.findingType,
            title: `Finding #${this.existingFindingsCount + 1}`,
            testRun: this.testRun,
            audit: this.audit,
            affectedSpecs: this.affectedSpecs,
            observationTargets: this.observationTargets,
            issueData: {}, // backend currently requires this
        };

        this.testService.createFinding(this.projectId, creationData).pipe(
            switchMap(findingId => this.testService.getFinding$(findingId, true).pipe(take(1))),
            take(1),
            finalize(() => this.creating = false)
        ).subscribe({
            next: (finding: Finding) => this.findingCreated.emit(finding),
            error: (error) => {
                console.error('Error creating new finding:', error);
                this.notificationService.toastErrorNotification('The finding could not be created.');
            }
        });
    }
}
